import * as Immutable from 'immutable';
import { IUserAddressStore } from './UserAddressesStore'
import { Price } from '../../UIData/_stores/UIDataStore';

export type CartItemType = "regular" | "pattern-design" | "sample-pack" | "sample-pack-natural" | "sample-pack-synthetic" | "sample-pack-natural-synthetic" | "other";
export type CartItemStatus = "cancelled" | "cart" | "ignored" | "in-production" | "on-hold" | "ordered" | "paid" | "ready" | "shipped";

export interface IBaseItem {
	type: CartItemType
	quantity: number
	price: Price
	fixedQuantity: boolean
}

const defaultBaseItem = Immutable.Record<IBaseItem>({
	type: null,
	quantity: 0,
	price: new Price(),
	fixedQuantity: false
});

export class BaseItem extends defaultBaseItem implements IBaseItem {}

export interface ICartItemFabric {
	id_fabric: number
	id_design: number
	id_size: number
}

const defaultCartItemFabric = Immutable.Record<ICartItemFabric>({
	id_fabric: null,
	id_design: null,
	id_size: null,
});

export class CartItemFabric extends defaultCartItemFabric implements ICartItemFabric {}

export interface IRegularItem extends IBaseItem {
	type: "regular"
	fabric: CartItemFabric
	fixedQuantity: false
}

const defaultRegularItem = Immutable.Record<IRegularItem>({
	type: "regular",
	quantity: 0,
	price: new Price(),
	fabric: new CartItemFabric(),
	fixedQuantity: false
});

export class RegularItem extends defaultRegularItem implements IRegularItem {}


export interface IPatternDesignItem extends IBaseItem {
	type: "pattern-design"
	fixedQuantity: true
}

const defaultPatternDesignItem = Immutable.Record<IPatternDesignItem>({
	type: "pattern-design",
	quantity: 0,
	price: new Price(),
	fixedQuantity: true
});

export class PatternDesignItem extends defaultPatternDesignItem implements IPatternDesignItem {}

export interface ISamplePackItem extends IBaseItem {
	type: "sample-pack"
	fixedQuantity: false
}

const defaultSamplePackItem = Immutable.Record<ISamplePackItem>({
	type: "sample-pack",
	quantity: 0,
	price: new Price(),
	fixedQuantity: false
});

export class SamplePackItem extends defaultSamplePackItem implements ISamplePackItem {}

export interface ISamplePackNaturalItem extends IBaseItem {
	type: "sample-pack-natural"
	fixedQuantity: false
}

const defaultSamplePackNaturalItem = Immutable.Record<ISamplePackNaturalItem>({
	type: "sample-pack-natural",
	quantity: 0,
	price: new Price(),
	fixedQuantity: false
});

export class SamplePackNaturalItem extends defaultSamplePackNaturalItem implements ISamplePackNaturalItem {}

export interface ISamplePackSyntheticItem extends IBaseItem {
	type: "sample-pack-synthetic"
	fixedQuantity: false
}

const defaultSamplePackSyntheticItem = Immutable.Record<ISamplePackSyntheticItem>({
	type: "sample-pack-synthetic",
	quantity: 0,
	price: new Price(),
	fixedQuantity: false
});

export class SamplePackSyntheticItem extends defaultSamplePackSyntheticItem implements ISamplePackSyntheticItem {}

export interface ISamplePackNaturalSyntheticItem extends IBaseItem {
	type: "sample-pack-natural-synthetic"
	fixedQuantity: false
}

const defaultSamplePackNaturalSyntheticItem = Immutable.Record<ISamplePackNaturalSyntheticItem>({
	type: "sample-pack-natural-synthetic",
	quantity: 0,
	price: new Price(),
	fixedQuantity: false
});

export class SamplePackNaturalSyntheticItem extends defaultSamplePackNaturalSyntheticItem implements ISamplePackNaturalSyntheticItem {}

function CreateCartItem<T extends IBaseItem>(contents: T): T extends { type: "regular"|"pattern-design"|"sample-pack"|"sample-pack-natural"|"sample-pack-synthetic"|"sample-pack-natural-synthetic" } ? RegularItem : BaseItem {
	let isRegular = (c:IBaseItem):c is IRegularItem => {return c.type == "regular"}
	let isPatternDesign = (c:IBaseItem):c is IPatternDesignItem => {return c.type == "pattern-design"}
	let isSamplePack = (c:IBaseItem):c is ISamplePackItem => {return c.type == "sample-pack"}
	let isSamplePackNatural = (c:IBaseItem):c is ISamplePackNaturalItem => {return c.type == "sample-pack-natural"}
	let isSamplePackSynthetic = (c:IBaseItem):c is ISamplePackSyntheticItem => {return c.type == "sample-pack-synthetic"}
	let isSamplePackNaturalSynthetic = (c:IBaseItem):c is ISamplePackNaturalSyntheticItem => {return c.type == "sample-pack-natural-synthetic"}

	if(isRegular(contents)) {
		return new RegularItem(contents) as any;
	} else if(isPatternDesign(contents)) {
		return new PatternDesignItem(contents) as any;
	} else if(isSamplePack(contents)) {
		return new SamplePackItem(contents) as any;
	} else if(isSamplePackNatural(contents)) {
		return new SamplePackNaturalItem(contents) as any;
	} else if(isSamplePackSynthetic(contents)) {
		return new SamplePackSyntheticItem(contents) as any;
	} else if(isSamplePackNaturalSynthetic(contents)) {
		return new SamplePackNaturalSyntheticItem(contents) as any;
	} else return new BaseItem(contents) as any;
}

export function CartItem(item:any) {
	let cartItem;
	if(item.type == "regular") {
		cartItem = CreateCartItem({
			type: "regular",
			quantity: item.quantity,
			price: new Price(item.price),
			fixedQuantity: false
		});

		cartItem = cartItem.withMutations((cartItem) => {
			cartItem.setIn(['fabric', 'id_fabric'], item.fabric.id_fabric);
			cartItem.setIn(['fabric', 'id_design'], item.fabric.id_design);
			cartItem.setIn(['fabric', 'id_size'], item.fabric.id_size);
		});
	} else if(item.type == "pattern-design") {
		cartItem = CreateCartItem({
			type: "pattern-design",
			quantity: item.quantity,
			price: new Price(item.price),
			fixedQuantity: true
		});
	} else if(item.type == "sample-pack") {
		cartItem = CreateCartItem({
			type: "sample-pack",
			quantity: item.quantity,
			price: new Price(item.price),
			fixedQuantity: false
		});
	} else if(item.type == "sample-pack-natural") {
		cartItem = CreateCartItem({
			type: "sample-pack-natural",
			quantity: item.quantity,
			price: new Price(item.price),
			fixedQuantity: false
		});
	} else if(item.type == "sample-pack-synthetic") {
		cartItem = CreateCartItem({
			type: "sample-pack-synthetic",
			quantity: item.quantity,
			price: new Price(item.price),
			fixedQuantity: false
		});
	} else if(item.type == "sample-pack-natural-synthetic") {
		cartItem = CreateCartItem({
			type: "sample-pack-natural-synthetic",
			quantity: item.quantity,
			price: new Price(item.price),
			fixedQuantity: false
		});
	} else {
		cartItem = CreateCartItem({
			type: "other",
			quantity: item.quantity,
			price: new Price(item.price),
			fixedQuantity: false
		});
	}
	return cartItem;
}

export interface ICartStore {
	ref_id: string,
	status: CartItemStatus,
	subtotal: Price,
	shipping: Price,
	taxes: Price,
	discount: Price,
	total: Price,
	date_created: Date,
	date_ordered: Date,
	date_paid: Date,
	date_ready: Date,
	date_shipped: Date,
	date_cancelled: Date,
	date_in_production: Date,
	id_shipping_address: number,
	id_billing_address: number,
	rush: boolean
	pickup: boolean
	id_shipping_type: number
	tracking_number: string
	items: Immutable.OrderedMap<string, BaseItem>
	availableShippingTypes: Immutable.OrderedMap<number, ShippingType>
}

const defaultStore = Immutable.Record<ICartStore>({
	ref_id: null,
	status: null,
	subtotal: new Price(),
	shipping: new Price(),
	taxes: new Price(),
	discount: new Price(),
	total: new Price(),
	date_created: null,
	date_ordered: null,
	date_paid: null,
	date_ready: null,
	date_shipped: null,
	date_cancelled: null,
	date_in_production: null,
	id_shipping_address: null,
	id_billing_address: null,
	id_shipping_type: null,
	rush: false,
	pickup: false,
	tracking_number: '',
	items: Immutable.OrderedMap<string, BaseItem>(),
	availableShippingTypes: Immutable.OrderedMap<number, ShippingType>()
});

export default class UserCartStore extends defaultStore implements ICartStore {
	constructor(params?:any) {
		if(params) {
			let store = new UserCartStore();
			store = store.withMutations(store => {
				if(params.hasOwnProperty('availableShippingTypes') && params.availableShippingTypes !== null) 
					store.set('availableShippingTypes', Immutable.OrderedMap<number, ShippingType>());
					for(let typeIndex in params.availableShippingTypes) {
						if(!params.availableShippingTypes.hasOwnProperty(typeIndex)) continue;
						let type = params.availableShippingTypes[typeIndex];
						store.setIn(['availableShippingTypes', type.id], new ShippingType(type));
					}

				if(params.hasOwnProperty('ref_id') && params.ref_id !== null) 
					store.set('ref_id', params.ref_id);

				if(params.hasOwnProperty('status') && params.status !== null) 
					store.set('status', params.status);

				if(params.hasOwnProperty('tracking_number') && params.tracking_number !== null) 
					store.set('tracking_number', params.tracking_number);

				if(params.hasOwnProperty('id_shipping_type') && params.id_shipping_type !== null) 
					store.set('id_shipping_type', Number(params.id_shipping_type));

				if(params.hasOwnProperty('id_shipping_address') && params.id_shipping_address !== null) 
					store.set('id_shipping_address', params.id_shipping_address);
				
				if(params.hasOwnProperty('id_billing_address') && params.id_billing_address !== null) 
					store.set('id_billing_address', params.id_billing_address);
				
				if(params.hasOwnProperty('pickup') && params.pickup !== null) 
					store.set('pickup', params.pickup == true);

				if(params.hasOwnProperty('rush') && params.rush !== null) 
					store.set('rush', params.rush == true);

				if(params.hasOwnProperty('date_created') && params.date_created !== null)
					store.set('date_created', new Date(params.date_created));

				if(params.hasOwnProperty('date_ordered') && params.date_ordered !== null)
					store.set('date_ordered', new Date(params.date_ordered));

				if(params.hasOwnProperty('date_paid') && params.date_paid !== null)
					store.set('date_paid', new Date(params.date_paid));

				if(params.hasOwnProperty('date_ready') && params.date_ready !== null)
					store.set('date_ready', new Date(params.date_ready));

				if(params.hasOwnProperty('date_shipped') && params.date_shipped !== null)
					store.set('date_shipped', new Date(params.date_shipped));

				if(params.hasOwnProperty('date_cancelled') && params.date_cancelled !== null)
					store.set('date_cancelled', new Date(params.date_cancelled));

				if(params.hasOwnProperty('date_in_production') && params.date_in_production !== null)
					store.set('date_in_production', new Date(params.date_in_production));

				if(params.hasOwnProperty('subtotal') && params.subtotal !== null)
					store.set('subtotal', new Price(params.subtotal));

				if(params.hasOwnProperty('shipping') && params.shipping !== null)
					store.set('shipping', new Price(params.shipping));

				if(params.hasOwnProperty('taxes') && params.taxes !== null)
					store.set('taxes', new Price(params.taxes));

				if(params.hasOwnProperty('discount') && params.discount !== null)
					store.set('discount', new Price(params.discount));

				if(params.hasOwnProperty('total') && params.total !== null)
					store.set('total', new Price(params.total));

				if(params.hasOwnProperty('items') && params.items !== null) {
					store.set('items', Immutable.OrderedMap<string, BaseItem>());
					for(let itemId in params.items) {
						if(!params.items.hasOwnProperty(itemId)) continue;
						const item = params.items[itemId];
						store.setIn(['items', itemId], CartItem(item));
					}
				}
			});

			super(store);
		} else super();
	}
}

export interface IShippingType {
	id: number,
	slug: string,
	ships_from: string
	name: string
	description: string
}

const defaultShippingTypeStore = Immutable.Record<IShippingType>({
	id: 0,
	slug: '',
	ships_from: '',
	name: '',
	description: '',
});

export class ShippingType extends defaultShippingTypeStore implements IShippingType {}